import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import gsap from 'gsap'
import { ScreenContext } from 'components/Providers'

import colors from "styles/colors";
import text from "styles/text";
import media from "styles/media";

import { useMedia, useStaggeredMovement } from "utils/hooks";
import { vwToPx } from "utils/functions";

import Banner from "components/Banner";

import PhonePNG from "assets/images/iphone.png";
import DiamondWEBP from "assets/images/diamond.webp";
import ArrowSVG from "assets/images/icons/bitArrow.svg";
import PhoneMP4 from 'assets/videos/tipping.mp4'
import Phone2MP4 from 'assets/videos/tipping2.mp4'

const Earn: React.FC = () => {

  const screen = useContext(ScreenContext)

  const [svg1Loaded, setSvg1Loaded] = useState<boolean>(false);
  const [svg2Loaded, setSvg2Loaded] = useState<boolean>(false);
  const [diamond1Ref, setDiamond1Ref] = useState<HTMLImageElement | null>(null);
  const [diamond2Ref, setDiamond2Ref] = useState<HTMLImageElement | null>(null);
  const [diamond3Ref, setDiamond3Ref] = useState<HTMLImageElement | null>(null);
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null)
  const [title1Ref, setTitle1Ref] = useState<HTMLHeadingElement | null>(null)
  const [text1Ref, setText1Ref] = useState<HTMLParagraphElement | null>(null)

  useStaggeredMovement([diamond1Ref, diamond2Ref, diamond3Ref]);

  const scrollOffset = useMedia(
    `${vwToPx(10)}, ${vwToPx(11)}`,
    `${(window.innerHeight / 2) - (vwToPx(41.67) / 2) - (vwToPx(14.86))}, ${vwToPx(30)}`,
    `-200, ${(window.innerWidth / 100) * 30}`,
    `110, ${(window.innerWidth / 100) * 90}`
  );

  useEffect(() => {
    if (scrollOffset) {
      window.locomotiveScroll.update()
    }
  }, [scrollOffset]) 

  useEffect(() => {
    if (videoRef && wrapperRef) {
      gsap.to(videoRef, {
        scrollTrigger: {
          scroller: '.smooth-scroll',
          trigger: wrapperRef,
          start: 'top+=20% top',
          end: 'bottom+=20% bottom',
          scrub: true
        },
        y: '-200%'
      })
    }
  }, [videoRef, wrapperRef])

  useEffect(() => {
    if (screen.mobile && title1Ref && text1Ref) {
      gsap.to([title1Ref, text1Ref], {
        scrollTrigger: {
          scroller: '.smooth-scroll',
          trigger: title1Ref,
          start: "top top+=25%",
          end: "top top-=5%",
          scrub: true,
        },
        y: `-${vwToPx(26.67)}px`
      })
    }
  }, [title1Ref, text1Ref, screen])

  return (
    <Wrapper id="Earn" ref={ref => setWrapperRef(ref)}>

      <PhoneWrapper
        data-scroll 
        data-scroll-sticky 
        data-scroll-target="#Earn" 
        data-scroll-offset={scrollOffset} 
      >
        <PhoneImage 
          src={PhonePNG} 
          alt="phone" 
          loading="lazy"
        />
        <PhoneVideo ref={ref => setVideoRef(ref)} muted autoPlay loop playsInline controls={false} preload="none">
          <source src={PhoneMP4} type="video/mp4"/>
        </PhoneVideo>
        <PhoneVideo2 muted autoPlay loop playsInline controls={false} preload="none">
          <source src={Phone2MP4} type="video/mp4"/>
        </PhoneVideo2>
      </PhoneWrapper>

      <Diamond1 src={DiamondWEBP} alt="diamond" ref={(ref) => setDiamond1Ref(ref)} loading="lazy"/>
      <Diamond2 src={DiamondWEBP} alt="diamond" ref={(ref) => setDiamond2Ref(ref)} loading="lazy"/>
      <Diamond3 src={DiamondWEBP} alt="diamond" ref={(ref) => setDiamond3Ref(ref)} loading="lazy"/>

      <Banner1Wrapper>
        <Banner background={colors.gold} toRight loaded={svg1Loaded}>
          <BannerInner>
            <BannerText>ENGAGE-AND-EARN</BannerText>
            <Arrow src={ArrowSVG} alt="arrow" onLoad={() => setSvg1Loaded(true)} />
          </BannerInner>
        </Banner>
      </Banner1Wrapper>

      <Banner2Wrapper>
        <Banner background={colors.horzReverseGradient} loaded={svg2Loaded}>
          <BannerInner>
            <BannerText>CREATE-AND-EARN</BannerText>
            <Arrow1 src={ArrowSVG} alt="arrow" onLoad={() => setSvg2Loaded(true)} />
          </BannerInner>
        </Banner>
      </Banner2Wrapper>

      <Title1 ref={ref => setTitle1Ref(ref)}>Create-and-earn</Title1>
      <Text1 ref={ref => setText1Ref(ref)}>FreshCut is pioneering a Create-and-Earn model that rewards creators with $FCD as they post content, engage with their fans, and contribute to the community.</Text1>
      <Title2 >Engage-and-earn</Title2>
      <Text2>
        FreshCut users can earn $FCD through the app, and also by attending FreshCut events and gaming tournaments. The more engaged you are with the community, the more $FCD you can earn.
      </Text2>
    </Wrapper>
  );
};

export default Earn;

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  background-color: ${colors.pureBlack};

  height: 124.1vw;

  ${media.tablet} {
    height: 145.586vw;
  }

  ${media.mobile} {
    height: 374.933vw;
    padding: 182.133vw 4vw 0;
  }
`;

const PhoneWrapper = styled.div`
  position: absolute;
  z-index: 3;
  border-radius: 3.47vw;
  overflow: hidden;
  transform: translate3d(0,0,0);

  left: calc(50% - 10.29vw);
  top: 14.86vw;
  width: 20.56vw;
  height: 41.67vw;

  ${media.tablet} {
    left: calc(50% - (29.102vw / 2));
    width: 29.102vw;
    height: 58.594vw;
    top: 7.813vw;
    border-radius: 4.9vw;
  }

  ${media.mobile} {
    width: 46.533vw;
    height: 90.667vw;
    top: 51.467vw;
    left: calc(50% - (46.533vw / 2));
    border-radius: 8.33vw;
  }
`

const PhoneImage = styled.img`
  position: absolute;
  pointer-events: none;
  z-index: 3;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  transform: translate3d(0, 0, 0);
`;

const PhoneVideo = styled.video`
  position: absolute;
  z-index: 2;
  width: 90%;
  height: 98%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const PhoneVideo2 = styled(PhoneVideo)`
  z-index: 1;
`

const Title = styled.h3`
  ${text.desktop.h4}
  position: absolute;
  color: ${colors.pureWhite};
  text-transform: uppercase;

  ${media.mobile} {
    ${text.mobile.h6}
    width: 80.933vw;
    position: relative;
  }
`;

const Text = styled.p`
  ${text.desktop.bodyR}
  position: absolute;
  color: ${colors.pureWhite};

  ${media.mobile} {
    position: relative;
    ${text.mobile.bodyS};
    width: 92vw;
  }
`;

const Title1 = styled(Title)`
  width: 35.9vw;
  left: 4.17vw;
  top: 23.54vw;

  ${media.tablet} {
    ${text.tablet.h4}
    left: 5.859vw;
    width: 48.945vw;
    top: 20.508vw;
    letter-spacing: -0.05em;
  }

  ${media.mobile} {
    position: relative;
    top: auto;
    left: auto;
    width: 80.933vw;
  }
`;

const Text1 = styled(Text)`
  width: 31.25vw;
  left: 4.17vw;
  top: 33.26vw;

  ${media.tablet} {
    ${text.tablet.bodyR}
    width: 43.945vw;
    left: 5.859vw;
  }

  ${media.mobile} {
    left: auto;
    top: auto;
    width: 92vw;
    margin-bottom: 100vw;
  }
`;

const Title2 = styled(Title)`
  width: 35.9vw;
  top: 79.79vw;
  right: 2.17vw;

  ${media.tablet} {
    ${text.tablet.h4}
    left: 5.859vw;
    width: 48.945vw;
    top: 92.773vw;
    letter-spacing: -0.05em;
  }

  ${media.mobile} {
    top: auto;
    right: auto;
    width: 80.933vw;
  }
`;

const Text2 = styled(Text)`
  width: 33.9vw;
  right: 4.17vw;
  top: 89.51vw;

  ${media.tablet} {
    ${text.tablet.bodyR}
    width: 45.117vw;
    left: 5.859vw;
    top: 106vw;
  }

  ${media.mobile} {
    position: relative;
    right: auto;
    top: auto;
    width: 92vw;
  }
`;

const Diamond = styled.img`
  position: absolute;
`;

const Diamond1 = styled(Diamond)`
  transform: rotate(-15deg);
  top: 10vw;
  left: 23.33vw;
  width: 21.5vw;

  ${media.tablet} {
    left: 33.203vw;
    top: 4vw;
  }

  ${media.mobile} {
    transform: rotate(15deg);
    width: 48vw;
    height: 37.333vw;
    top: 34.667vw;
    left: -25.8vw;
  }
`;

const Diamond2 = styled(Diamond)`
  transform: rotate(15deg);
  top: 20.69vw;
  right: 26.74vw;
  width: 12.25vw;

  ${media.tablet} {
    right: -5.883vw;
    top: 16vw;
  }

  ${media.mobile} {
    width: 24vw;
    height: 18.667vw;
    top: 70.4vw;
    right: -12vw;
  }
`;

const Diamond3 = styled(Diamond)`
  transform: rotate(135deg);
  top: 46.04vw;
  right: 33.82vw;
  width: 5.13vw;

  ${media.tablet} {
    right: 6.094vw;
    top: 55vw;
  }

  ${media.mobile} {
    width: 12vw;
    height: 9.333vw;
    right: 13.6vw;
    top: 167.733vw;
  }
`;

const BannerInner = styled.div`
  height: 100%;
  width: fit-content;
  display: flex;
  align-items: center;

  padding-top: 0.83vw;
  padding-bottom: 0.83vw;
  
  ${media.mobile} {
    padding: 2vw 0;
  }
`;

const BannerText = styled.p`
  ${text.desktop.h6}
  color: ${colors.pureBlack};

  margin-right: 1.04vw;

  ${media.tablet} {
    ${text.tablet.h5}
    margin: 0.488vw 0.488vw;
  }

  ${media.mobile} {
    font-size: 4vw;
    margin-right: 4vw;
  }
`;

const Arrow = styled.img`
  transform: rotate(-90deg);
  filter: grayscale(1) brightness(0);

  width: 1.74vw;
  height: 1.74vw;
  margin-right: 1.04vw;

  rect {
    fill: ${colors.pureBlack};
  }

  ${media.tablet} {
    width: 2.734vw;
    height: 2.148vw;
    margin: 0.977vw 1.465vw;
  }
  
  ${media.mobile} {
    width: 3.733vw;
    height: 2.933vw;
    margin-right: 4vw;
  }
`;

const Arrow1 = styled(Arrow)`
  transform: rotate(90deg);
`;

const Banner1Wrapper = styled.div`
  position: absolute;
  transform-origin: left;
  transform: rotate(30deg) translate3d(0,0,0);
  z-index: 2;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 1% 100%);

  top: 68.3vw;
  left: 6.82vw;

  ${media.tablet} {
    top: 65vw;
    left: 15vw;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0.75% 100%);
  }

  ${media.mobile} {
    top: 255.2vw;
    clip-path: polygon(0 0,100% 0,100% 100%,.76% 100%);
    left: 15.8vw;
  }
`;

const Banner2Wrapper = styled.div`
  position: absolute;
  transform-origin: left;
  transform: rotate(-30deg) translate3d(0,0,0);
  clip-path: polygon(1% 0, 100% 0, 100% 100%, 0 100%);

  top: 69.3vw;
  left: 6.9vw;

  ${media.tablet} {
    top: 67vw;
    left: 14.4vw;
    clip-path: polygon(1% 0, 100% 0, 100% 100%, 0.35% 100%);
  }
  
  ${media.mobile} {
    top: 258vw;
    left: 15vw;
    clip-path: polygon(1% 0, 100% 0, 100% 100%, 0.15% 100%);
  }
`;
